"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { useUserRFQProjects } from "@/modes/agentic-workflows/rfq/hooks/useRFQProject";
import { getStateLabel, getPhaseFromState } from "@/modes/agentic-workflows/rfq/state-machine";
import { getUserProjectRole } from "@/modes/agentic-workflows/rfq/permissions";
import type { WorkflowState } from "@/modes/agentic-workflows/rfq/types";

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function stateColor(state: WorkflowState): string {
  if (state === "CANCELLED") return "#ef4444";
  if (state === "CLOSED" || state === "ARCHIVED") return "var(--ink-mute)";
  if (state === "SUPPLIERS_NOTIFIED") return "#10B981";
  if (state.includes("APPROVED")) return "#A855F7";
  if (state.includes("QUOTE") || state.includes("NEGOTIATION")) return "#F59E0B";
  if (state.includes("EVALUATION") || state.includes("CONSENSUS") || state.includes("AWARD")) return "#3B82F6";
  if (state.includes("RFQ") || state.includes("QNA")) return "#6366F1";
  return "var(--indigo)";
}

export default function RFQIndexPage() {
  const router = useRouter();
  const { user } = useAuthContext();
  const { projects, loading, deleteProject } = useUserRFQProjects(user?.uid ?? null, user?.email ?? null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.project_name.toLowerCase().includes(q);
  });

  const handleDelete = async (e: React.MouseEvent, wid: string) => {
    e.stopPropagation();
    setDeleting(wid);
    try {
      await deleteProject(wid);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="diag-hero">
        <div className="diag-hero-icon" aria-hidden="true">
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <defs>
              <linearGradient id="rfq-hero-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#5B6CFF"/>
                <stop offset="100%" stopColor="#EC4899"/>
              </linearGradient>
            </defs>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="url(#rfq-hero-grad)"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="url(#rfq-hero-grad)"/>
            <line x1="12" y1="22.08" x2="12" y2="12" stroke="url(#rfq-hero-grad)"/>
          </svg>
        </div>
        <h1><span className="grad-text">Sourcing</span> RFQ Workflow</h1>
        <p className="diag-hero-sub">End-to-end RFQ lifecycle — from creation to supplier notification</p>
      </section>

      <div className="diag-page">
        {/* Toolbar */}
        <div className="rfp-toolbar">
          <div className="search-box" style={{ flex: 1 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="search"
              placeholder="Search RFQ events…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => router.push("/agentic-workflows/rfq/new")}
            className="rfp-new-btn"
          >
            + New RFQ Event
          </button>
        </div>

        {/* States */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "var(--ink-mute)" }}>Loading…</div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <p style={{ color: "var(--ink-mute)", marginBottom: "20px" }}>No RFQ events yet</p>
            <button
              onClick={() => router.push("/agentic-workflows/rfq/new")}
              style={{
                background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
                border: "none", borderRadius: "100px", padding: "10px 24px",
                fontSize: "14px", fontWeight: 600, color: "#fff", cursor: "pointer",
              }}
            >
              Create Your First RFQ Event
            </button>
          </div>
        ) : (
          <div className="diag-grid">
            {filtered.map((project) => {
              const phase = getPhaseFromState(project.workflow_state);
              const role = user ? getUserProjectRole(project, user.email ?? "") : "none";
              const isOwner = project.created_by === user?.uid;
              const color = stateColor(project.workflow_state);

              const invitedSupplier = user
                ? project.step_07_publish_rfq?.suppliers?.find((s) => s.email === user.email)
                : undefined;
              const displayRole = invitedSupplier ? "supplier" : role;
              const onClick = invitedSupplier?.token
                ? () => router.push(`/supplier/rfq/${project.wid}/${invitedSupplier.token}`)
                : () => router.push(`/agentic-workflows/rfq/${project.wid}`);

              return (
                <article
                  key={project.wid}
                  className="diag-card d-supplychain"
                  onClick={onClick}
                  style={{ cursor: "pointer" }}
                >
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {project.project_name}
                      </h3>
                      <p style={{ fontSize: "13px", color: "var(--ink-mute)", margin: 0 }}>
                        Phase {phase} of 4 · {(project.industry_vertical || "general").replace(/_/g, " ")}
                        {displayRole !== "none" && displayRole !== "owner" && <> · <span style={{ textTransform: "capitalize" }}>{displayRole}</span></>}
                      </p>
                    </div>
                    <span style={{
                      fontSize: "11px", fontWeight: 700, padding: "4px 10px",
                      borderRadius: "100px", whiteSpace: "nowrap", flexShrink: 0,
                      background: `${color}22`,
                      color: color,
                      border: `1px solid ${color}44`,
                    }}>
                      {getStateLabel(project.workflow_state)}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: "3px", borderRadius: "4px", background: "var(--line)", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", width: `${(phase / 4) * 100}%`,
                      background: "linear-gradient(90deg, var(--indigo), var(--magenta))",
                      borderRadius: "4px",
                    }} />
                  </div>

                  {/* Footer */}
                  <div className="card-foot" style={{ marginTop: "auto" }}>
                    <span className="card-time">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {timeAgo(project.updated_at)}
                    </span>

                    {isOwner && (
                      <button
                        className="card-cta"
                        onClick={(e) => handleDelete(e, project.wid)}
                        disabled={deleting === project.wid}
                        style={{ borderColor: "rgba(239,68,68,0.3)" }}
                      >
                        {deleting === project.wid ? "…" : "Delete"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {filtered.length === 0 && projects.length > 0 && (
          <div className="diag-empty"><p>No RFQ events match your search.</p></div>
        )}
      </div>
    </>
  );
}
